import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import config from '../../config';
import {
  REMOTE_SUPPORT_STATUS_ALL,
  REMOTE_SUPPORT_STATUS,
  ROLE,
} from '../../lib/constants';
import {
  Main,
  InputWrapper,
  Input,
  Select,
  SearchBoxWrapper,
} from './styles/SearchBoxStyles';

/**
 * ユーザー管理画面の検索ボックス
 *
 * @param {Object} props
 * @param {number} props.role - 現在のユーザーのロール
 * @param {number} props.selectedCompanyId - 選択された会社のID
 * @param {string} props.namePartial - 入力された部分的なユーザー名
 * @param {number} props.selectedStatus
 * @param {number} props.selectedRemoteSupportStatus
 * @param {Object[]} props.companies - 会社のリスト
 * @param {Function} props.onSelectCompany - 会社選択時のコールバック関数
 * @param {Function} props.onSelectRole - ロール選択時のコールバック関数
 * @param {Function} props.onSelectStatus - アカウントステータス選択時のコールバック関数
 * @param {Function} props.onSelectRemoteSupportStatus - リモートサポートステータス選択時のコールバック関数
 * @param {Function} props.onSearchUserName - ユーザー名検索時のコールバック関数
 * @param {boolean} props.displaySearchOption - 会社選択オプションを表示するかどうか
 * @param {boolean} props.displaySearchUserName - ユーザー名検索オプションを表示するかどうか
 * @param {boolean} props.displaySearchUserRole - 権限絞り込みを表示するかどうか
 * @param {boolean} props.displaySearchAccountStatus - 状態で絞り込みを表示するかどうか
 * @param {boolean} props.displaySearchRemoteSupportStatus - 遠隔支援状態で絞り込みを表示するかどうか
 * @returns {ReactElement}
 */
const SearchBox = ({
  role,
  selectedCompanyId,
  namePartial,
  selectedStatus,
  selectedRemoteSupportStatus,
  companies,
  onSelectCompany,
  onSelectRole,
  onSelectStatus,
  onSelectRemoteSupportStatus,
  onSearchUserName,
  displaySearchOption,
  displaySearchUserName,
  displaySearchUserRole,
  displaySearchAccountStatus,
  displaySearchRemoteSupportStatus,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const defaultOption = formatMessage({ id: 'staff.select.default' });
  const defaultRoleOption = formatMessage({ id: 'role.select.default' });
  const defaultStatusOption = formatMessage({
    id: 'operator.OperatorLists.accountStatus.select.default',
  });
  const defaultRemoteSupportStatusOption = formatMessage({
    id: 'operator.OperatorLists.remoteSupportStatus.select.default',
  });
  const userSelectPlaceholder = formatMessage({
    id: 'operator.select.default',
  });
  const roles = config.customRole.searchRole[role];
  const selectCompany = _companies =>
    _companies.map(company => {
      const { companyName } = company;
      return (
        <option value={company.companyId} key={`select_${company.companyId}`}>
          {companyName}
        </option>
      );
    });
  const selectRole = _roles =>
    _roles.map(roleId => {
      const roleName = formatMessage({ id: `operator.role.status.${roleId}` });
      return (
        <option value={roleId} key={`roleSelect_${roleId}`}>
          {roleName}
        </option>
      );
    });
  const selectStatus = () =>
    config.searchAccountStatus.all.map(status => {
      const statusName = formatMessage({
        id: `operator.OperatorLists.accountStatus.select.${status}`,
      });
      return (
        <option value={status} key={`accountStatusSelect_${status}`}>
          {statusName}
        </option>
      );
    });
  const selectRemoteSupportStatus = () =>
    REMOTE_SUPPORT_STATUS_ALL.map(status => {
      const statusName = formatMessage({
        id: `operator.OperatorLists.remoteSupportStatus.select.${status}`,
      });
      return (
        <option value={status} key={`remoteSupportStatusSelect_${status}`}>
          {statusName}
        </option>
      );
    });
  return (
    <Main className="staffTitle" id="StaffPageSearchBox">
      <div>
        <SearchBoxWrapper>
          {displaySearchUserName && (
            <InputWrapper>
              <FormattedMessage id="operator.select.title" />
              <Input
                id="searchUserName"
                placeholder={userSelectPlaceholder}
                defaultValue={namePartial}
                onChange={e => onSearchUserName(e.target.value)}
              />
            </InputWrapper>
          )}
          {displaySearchOption && (
            <InputWrapper>
              <Select
                id="selectCompany"
                defaultValue={selectedCompanyId}
                onChange={e => onSelectCompany(e.target.value)}
              >
                <option key="default" value={0}>
                  {defaultOption}
                </option>
                {selectCompany(companies)}
              </Select>
            </InputWrapper>
          )}
          {displaySearchUserRole && (
            <InputWrapper>
              <Select
                id="selectRole"
                defaultValue={ROLE.ALL}
                onChange={e => onSelectRole(e.target.value)}
              >
                <option key="default" value={ROLE.ALL}>
                  {defaultRoleOption}
                </option>
                {selectRole(roles)}
              </Select>
            </InputWrapper>
          )}
          {displaySearchAccountStatus && (
            <InputWrapper>
              <Select
                id="selectAccountStatus"
                defaultValue={selectedStatus}
                onChange={e => onSelectStatus(e.target.value)}
              >
                <option key="default" value={config.accountStatus.ALL}>
                  {defaultStatusOption}
                </option>
                {selectStatus()}
              </Select>
            </InputWrapper>
          )}
          {displaySearchRemoteSupportStatus && (
            <InputWrapper>
              <Select
                id="selectRemoteSupportStatus"
                defaultValue={selectedRemoteSupportStatus}
                onChange={e => onSelectRemoteSupportStatus(e.target.value)}
              >
                <option key="default" value={REMOTE_SUPPORT_STATUS.all}>
                  {defaultRemoteSupportStatusOption}
                </option>
                {selectRemoteSupportStatus()}
              </Select>
            </InputWrapper>
          )}
        </SearchBoxWrapper>
      </div>
    </Main>
  );
};

SearchBox.propTypes = {
  role: PropTypes.number.isRequired,
  selectedCompanyId: PropTypes.number.isRequired,
  namePartial: PropTypes.string.isRequired,
  selectedStatus: PropTypes.number.isRequired,
  selectedRemoteSupportStatus: PropTypes.number.isRequired,
  companies: PropTypes.array.isRequired,
  onSelectCompany: PropTypes.func.isRequired,
  onSelectRole: PropTypes.func.isRequired,
  onSelectStatus: PropTypes.func.isRequired,
  onSelectRemoteSupportStatus: PropTypes.func.isRequired,
  onSearchUserName: PropTypes.func.isRequired,
  displaySearchOption: PropTypes.bool.isRequired,
  displaySearchUserName: PropTypes.bool.isRequired,
  displaySearchUserRole: PropTypes.bool.isRequired,
  displaySearchAccountStatus: PropTypes.bool.isRequired,
  displaySearchRemoteSupportStatus: PropTypes.bool.isRequired,
};

export default SearchBox;
